import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { type SupplierDetailResponse } from '../types';
import { zodSupplierDetailResponseSchema } from '../validations';

import { type AddSupplierResponsiblePersonPayload } from './types';
import { zodAddSupplierResponsiblePersonPayloadSchema } from './validations';

/**
 * Adds a responsible person to a supplier relationship, scoped to one
 * customer company or to the whole customer account. 409 if that person is
 * already responsible.
 *
 * @param deId - The data exchange ID of the supplier relationship.
 * @param payload - The user ID to add as a responsible person.
 * @param options - Optional ky request options (e.g., abort signal).
 * @returns The refreshed supplier detail response — no follow-up read is needed.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/add_supplier_responsible_person_suppliers__de_id__responsible_persons_post
 */
export const addSupplierResponsiblePerson = async (
  deId: UUID,
  payload: AddSupplierResponsiblePersonPayload,
  options?: KyRequestOptions,
) =>
  vestigasApi
    .post(ENDPOINT.SUPPLIER.ADD_RESPONSIBLE_PERSON(deId), {
      json: payload,
      ...options,
      validate: {
        body: zodAddSupplierResponsiblePersonPayloadSchema,
        response: zodSupplierDetailResponseSchema,
      },
    })
    .json<SupplierDetailResponse>();
